import React, { useState, useMemo, useCallback, useEffect } from 'react';
import JSZip from 'jszip';
import superagent from 'superagent';
import Loading from '../loading/Loading';
import Collapsible from '../Collapsible';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { buildRouteName } from '../../utils/utils';

import config from '../../config/kystverket';

function getRtzUrl(routeNumber) {
  return config.adaptive4Url + 'api/kyv/routeinfo/v1/rtz/routes/' + routeNumber + '/download';
}

const LocationsAfter = ({ lat, lon }) => {
  const [, setDownloading] = useState(false);
  const [, setDownloadError] = useState(null);
  const [routesList, setRoutesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchRoutes() {
      const getRoutesUrl =
        config.adaptive4Url + 'api/kyv/routeinfo/v1/rtz/routes/list/' + lat + '/' + lon;
      try {
        setLoading(true);
        const response = await superagent.get(getRoutesUrl);
        if (response.body) {
          setRoutesList(response.body);
        }
      } catch (error) {
        console.error('Error fetching routes:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchRoutes();
  }, [lat, lon]);

  const zipEntries = useCallback((zip, rtz, entries, index = 0) => {
    const entry = entries[index];

    if (index === 0) {
      setDownloading(true);
      setDownloadError(null);
    }

    if (index === entries.length) {
      zip.generateAsync({ type: 'blob' }).then(blob => {
        saveAs(blob, rtz + '_routes.zip');
        setDownloading(false);
      });
      return;
    }

    superagent
      .get(entry.url)
      .responseType('blob')
      .end((err, res) => {
        if (err) {
          setDownloading(false);
          setDownloadError(err);
          return;
        }
        zip.file(entry.title, res.body);
        zipEntries(zip, rtz, entries, index + 1);
      });
  }, []);

  const onDownloadRoutesRtz = useCallback(
    rtz => {
      const entries = [];
      const cbs = document.getElementsByClassName('routeRtzDownload');
      for (var i = 0; i < cbs.length; ++i) {
        const cb = cbs[i];
        const routeNumber = cb.attributes['data-' + rtz].value;
        const title = cb.attributes['data-title'].value;
        if (cb.checked && routeNumber) {
          entries.push({
            url: getRtzUrl(routeNumber),
            title: title
          });
        }
      }

      if (entries.length) {
        const zip = new JSZip();
        zipEntries(zip, rtz, entries);
      }
    },
    [zipEntries]
  );

  const routes = useMemo(() => {
    const gr = {
      in: [],
      out: [],
      between: [],
      opensea: []
    };

    if (!routesList) return;

    routesList.forEach(r => {
      var dir = parseInt(r.routenumber.substring(4, 5));
      switch (dir) {
        case 1:
          gr.in.push(r);
          break;
        case 2:
          gr.out.push(r);
          break;
        case 3:
          gr.between.push(r);
          break;
        case 4:
        case 5:
          gr.opensea.push(r);
          break;
        default:
          break;
      }
    });

    return gr;
  }, [routesList]);

  const renderRoute = useCallback(
    route => {
      const { routename, routenumber, validityperiodstart } = route;
      const route_name = buildRouteName({
        routeQualityStatus: 'NCA',
        routeName: routename,
        date: validityperiodstart
      });
      return (
        <p key={routenumber} className="subList">
          <label title={route_name}>
            <input
              type="checkbox"
              className="routeRtzDownload"
              data-title={route_name}
              data-rtz10={routenumber}
            />
            {route_name}
          </label>
          <button
            className="btn btn-link"
            onClick={() => navigate('/view/route/' + routenumber)}
            title={'Show route details for route ' + route_name}
          >
            Go to route
          </button>
        </p>
      );
    },
    [navigate]
  );

  return (
    <div>
      {loading && <Loading />}
      <h4>Routes</h4>
      {loading ? (
        <p>Loading...</p>
      ) : routesList && routesList.length ? (
        <>
          <Collapsible
            key="in"
            header={`In (${routes.in.length})`}
          >
            {routes.in.map(r => renderRoute(r))}
          </Collapsible>
          <Collapsible
            key="out"
            header={`Out (${routes.out.length})`}
          >
            {routes.out.map(r => renderRoute(r))}
          </Collapsible>
          <Collapsible
            key="between"
            header={`Between (${routes.between.length})`}
          >
            {routes.between.map(r => renderRoute(r))}
          </Collapsible>
          <Collapsible
            key="opensea"
            header={`Open sea and Inshore (${routes.opensea.length})`}
          >
            {routes.opensea.map(r => renderRoute(r))}
          </Collapsible>
        </>
      ) : (
        'No routes'
      )}
      {routesList && routesList.length > 0 && (
        <div className="locations-after--download-btn-container">
          <p>Download RTZ for selected routes:</p>
          <p>
            <button className="btn btn-info" onClick={() => onDownloadRoutesRtz('rtz10')}>
              Download RTZ 1.0
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationsAfter;
